/**
 * OAuth Callback Page - Handle OAuth code exchange
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ApiError } from '../lib/api';
import { VerifyState } from '../types/auth-states';

export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { exchangeOAuthCode, isAuthenticated } = useAuth();
  const [state, setState] = useState<VerifyState>(VerifyState.Loading);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');

    if (!code) {
      setState(VerifyState.Error);
      setError('Invalid or missing authorization code');
      return;
    }

    const exchangeCode = async () => {
      try {
        await exchangeOAuthCode(code);
        setState(VerifyState.Success);
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
      } catch (err) {
        setState(VerifyState.Error);
        if (err instanceof ApiError) {
          if (err.code === 'auth_code_expired') {
            setError('Authorization code has expired. Please try signing in again.');
          } else if (err.code === 'auth_code_invalid') {
            setError('Invalid authorization code. Please try signing in again.');
          } else {
            setError(err.message);
          }
        } else {
          setError('Authentication failed. Please try again.');
        }
      }
    };

    exchangeCode();
  }, [searchParams, navigate, exchangeOAuthCode]);

  useEffect(() => {
    if (isAuthenticated && state !== VerifyState.Loading) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, state, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-600 rounded-xl mb-4">
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Resolve AI
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              AI-Powered Bug Resolution Platform
            </p>
          </div>

          {state === VerifyState.Loading && (
            <div className="text-center py-4">
              <Loader2 className="w-16 h-16 text-indigo-600 mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Signing you in...</h2>
              <p className="text-gray-600 dark:text-gray-400">Please wait while we complete authentication.</p>
            </div>
          )}

          {state === VerifyState.Success && (
            <div className="text-center py-4">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Success!</h2>
              <p className="text-gray-600 dark:text-gray-400">Redirecting to dashboard...</p>
            </div>
          )}

          {state === VerifyState.Error && (
            <div className="text-center py-4">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Authentication Failed</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
              <button
                onClick={() => navigate('/login', { replace: true })}
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-all shadow-lg hover:shadow-xl"
              >
                Back to Login
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-8">
          &copy; {new Date().getFullYear()} Resolve AI. All rights reserved.
        </p>
      </div>
    </div>
  );
}
